/**
 * AgentDashboard Component
 * AIDLC Administrative Backend
 *
 * Landing page / home view.
 * Displays all agents in a responsive grid of AgentStatusCards.
 * Grid: 3 columns on xl, 2 on md, 1 on mobile.
 *
 * Per the Architecture Document, this is Module 01: Agent Dashboard.
 * Shows real-time agent status with summary statistics at the top.
 *
 * Bridge Integration:
 *   When the bridge is active, agent statuses update in real-time
 *   via WebSocket events pushed from the bridge file watcher.
 *   The LIVE badge and "last updated" timestamp reflect bridge activity.
 *
 * Status values aligned with AgentStatus type:
 *   'working' | 'available' | 'waiting' | 'away'
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgents } from '../../hooks/useAgents';
import { useBridge } from '../layout/MainLayout';
import { AgentStatusCard } from './AgentStatusCard';
import { ApprovalPanel } from './ApprovalPanel';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import type { AgentStatus } from '../../types/agent';

type FilterOption = 'all' | AgentStatus;

export function AgentDashboard() {
  const navigate = useNavigate();
  const { socket } = useBridge();
  const { agents, isLoading, error, refresh, lastBridgeUpdate, bridgeActive } = useAgents(
    30000, // 30s poll
    socket,
  );
  const [filter, setFilter] = useState<FilterOption>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const summary = useMemo(() => {
    const working = agents.filter((a) => a.status === 'working').length;
    const available = agents.filter((a) => a.status === 'available').length;
    const waiting = agents.filter((a) => a.status === 'waiting').length;
    const away = agents.filter((a) => a.status === 'away').length;
    const offline = agents.filter((a) => a.status === 'offline').length;
    const totalTokens = agents.reduce((sum, a) => sum + a.statistics.totalTokensUsed, 0);
    return { working, available, waiting, away, offline, total: agents.length, totalTokens };
  }, [agents]);

  const filteredAgents = useMemo(() => {
    let result = agents;
    if (filter !== 'all') {
      result = result.filter((a) => a.status === filter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.role.toLowerCase().includes(q) ||
          a.expertise.some((e) => e.toLowerCase().includes(q))
      );
    }
    return result;
  }, [agents, filter, searchQuery]);

  if (isLoading) {
    return <LoadingSpinner message="Loading agents..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-700 text-sm font-medium">Failed to load agents</p>
        <p className="text-red-600 text-xs mt-1">{error}</p>
        <button
          onClick={refresh}
          className="mt-3 px-4 py-2 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Approval Requests */}
      <ApprovalPanel />

      {/* Bridge Status Banner */}
      {bridgeActive && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
          </span>
          <span className="text-xs text-green-700 font-medium">
            Bridge Active -- Agent statuses update in real-time from Claude Code sessions
          </span>
          {lastBridgeUpdate && (
            <span className="text-xs text-green-500 ml-auto">
              Last activity: {new Date(lastBridgeUpdate).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}

      {/* Summary Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-ldaja-black">{summary.total}</div>
          <div className="text-xs text-gray-500 mt-1">Total Agents</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{summary.working}</div>
          <div className="text-xs text-gray-500 mt-1">Working</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-ldaja-blue">{summary.available}</div>
          <div className="text-xs text-gray-500 mt-1">Available</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600">{summary.waiting}</div>
          <div className="text-xs text-gray-500 mt-1">Waiting</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-400">{summary.offline}</div>
          <div className="text-xs text-gray-500 mt-1">Offline</div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 w-full sm:max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 focus:border-ldaja-blue"
          />
        </div>

        {/* Status Filters */}
        <div className="flex items-center gap-1">
          {([
            { key: 'all', label: 'All', count: summary.total },
            { key: 'working', label: 'Working', count: summary.working },
            { key: 'available', label: 'Available', count: summary.available },
            { key: 'waiting', label: 'Waiting', count: summary.waiting },
            { key: 'away', label: 'Away', count: summary.away },
            { key: 'offline', label: 'Offline', count: summary.offline },
          ] as const).map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === key
                  ? 'bg-ldaja-blue text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>

        {/* Refresh */}
        <button
          onClick={refresh}
          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          title="Refresh agent data"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Agent Grid */}
      {filteredAgents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredAgents.map((agent) => (
            <AgentStatusCard key={agent.id} agent={agent} onClick={(agentId) => navigate(`/agents/${agentId}`)} />
          ))}
        </div>
      ) : agents.length === 0 ? (
        <EmptyState
          title="No agents registered"
          description="Configure agents in Settings to get started with the AIDLC system."
        />
      ) : (
        <EmptyState
          title="No agents match your filters"
          description="Try adjusting your search query or status filter."
          action={
            <button
              onClick={() => {
                setFilter('all');
                setSearchQuery('');
              }}
              className="text-sm text-ldaja-blue hover:underline"
            >
              Clear filters
            </button>
          }
        />
      )}
    </div>
  );
}
